import { ReactNode, useEffect } from "react";

import type { MainPanelKey, TabOption } from "@/panels/modules/panels/store/panels.store.types";

import { calcBothTabsHaveContent } from "@/panels/modules/panels/components/PanelsView.helpers";
import { usePanelsStore } from "@/panels/modules/panels/store/panels.store";
import { getOtherTab } from "@/panels/modules/panels/store/panels.store.helpers";
import { hasRenderableContent } from "@/utils/hasRenderableContent";

import { MainPanelContent } from "./MainPanelContent";

interface CollapsiblePanelProps {
    panelId: MainPanelKey;
    tabASlot?: ReactNode;
    tabBSlot?: ReactNode;
}

export const MainPanel = ({ panelId, tabASlot, tabBSlot }: CollapsiblePanelProps) => {
    const store = usePanelsStore();
    const panelConfig = store[panelId];

    // This is an important slot content effect:
    // If the currently selected tab has no renderable content, but the other tab does,
    // we automatically switch to the other tab. This fixes scenarios where conditionally
    // rendered content leaves a tab, so the tab header is removed, but the tab is still
    // selected. In that case, the user would see an empty panel with no tab headers,
    // having no way to switch to the other tab that does have content.
    useEffect(() => {
        const currentSelectedTab = panelConfig.selectedTab;
        const currentSelectedTabContent = currentSelectedTab === "a" ? tabASlot : tabBSlot;
        const otherTab = getOtherTab(currentSelectedTab);
        const otherTabContent = otherTab === "a" ? tabASlot : tabBSlot;

        if (!hasRenderableContent(currentSelectedTabContent) && hasRenderableContent(otherTabContent)) {
            store.setSelectedTab(panelId)(otherTab);
        }
    }, [tabASlot, tabBSlot, panelId, store, panelConfig]);

    const bothTabsHaveContent = calcBothTabsHaveContent({
        tabASlot: tabASlot,
        isTabAClosed: panelConfig.a.isClosed,
        tabBSlot: tabBSlot,
        isTabBClosed: panelConfig.b.isClosed,
    });

    const onTabChange = (tab: TabOption) => {
        store.setSelectedTab(panelId)(tab);
        panelConfig.onTabChange?.(tab);
    };

    const onCloseTab = (tab: TabOption) => {
        // If there's a custom onClose callback on the tab, use it instead of default behavior
        if (panelConfig[tab].onClose) {
            panelConfig[tab].onClose({ panelId, tab, isCurrentlyClosed: panelConfig[tab].isClosed });
        } else {
            // Default behavior: toggle the isClosed state
            store.setIsClosed(panelId)(tab)(!panelConfig[tab].isClosed);
        }
    };

    const onMinimizeTab = (tab: TabOption) => {
        store.setIsMinimized(panelId)(tab)(!panelConfig[tab].isMinimized);
    };

    const showHeader =
        panelConfig.selectedTab === "a"
            ? hasRenderableContent(tabASlot) && panelConfig.a.showHeader
            : hasRenderableContent(tabBSlot) && panelConfig.b.showHeader;

    return (
        <MainPanelContent
            mainPanelConfig={panelConfig}
            showTabBar={bothTabsHaveContent}
            onTabChange={onTabChange}
            onCloseTab={onCloseTab}
            onMinimizeTab={onMinimizeTab}
            tabASlot={tabASlot}
            tabBSlot={tabBSlot}
            showHeader={showHeader}
        />
    );
};
